/**
 * Recovery Debt Tracking System
 * 
 * Monitors cumulative fatigue using:
 * - Acute:Chronic Workload Ratio (ACWR)
 * - Composite recovery debt score
 * - Smart deload triggers
 * - Force balance tracking (push/pull ratio)
 * 
 * Based on sports science research for injury prevention and performance optimization
 */

import { getSessionHistory, type SessionLog } from './sessionStorage';
import { getExerciseMetadata } from './exerciseDatabase';
import { computeSetVolume } from './volumeTracking';
import { getACWRThresholds, getACWRStatus, type ExperienceLevel } from './acwrThresholds';
import { calculateTotalSessionsTonnage } from './volumeTracking';
import { db } from './db';
import { getCurrentUserId } from './anonymousAuth';

/**
 * ACWR tracing. This module logged eight lines to the console on EVERY Stats
 * load in shipped builds — including the silent visibilitychange reloads — so a
 * beta user's console filled with training internals. The traces are genuinely
 * useful while working on the model, so they are gated rather than deleted.
 */
const debugLog: (...args: unknown[]) => void =
  process.env.NODE_ENV === 'production' ? () => {} : (...args) => console.log(...args);

export interface RecoveryDebtScore {
  // Overall scores
  overallDebt: number;          // 0-100 (0=optimal, 100=severe overtraining)
  debtLevel: 'low' | 'moderate' | 'high' | 'severe';
  
  // ACWR (Acute:Chronic Workload Ratio)
  acwr: number;                 // Ratio: 0.8-1.3 is optimal
  acwrStatus: 'undertrained' | 'optimal' | 'elevated' | 'dangerous';
  acwrDataSufficient: boolean;  // True when 4+ training days exist in 28d window
  acuteLoad: number;            // Acute internal load (weekly-equivalent, AU)
  chronicLoad: number;          // Chronic internal load (weekly-equivalent, AU)
  
  // Volume trends
  weeklyVolume: number;         // Total tonnage this week (lbs)
  last4WeekAverage: number;     // 4-week rolling average
  volumeTrend: 'increasing' | 'stable' | 'decreasing';
  
  // Force balance
  forceBalance: {
    pushVolume: number;
    pullVolume: number;
    ratio: number;              // Push:Pull ratio (ideal: 0.8-1.2)
    status: 'balanced' | 'push-dominant' | 'pull-dominant';
    recommendation?: string;
  };
  
  // Recovery signals
  avgRPE: number;               // Average RPE across recent sessions
  rpeStatus: 'sustainable' | 'elevated' | 'unsustainable';
  consecutiveHighRPEDays: number;
  
  // Recommendations
  needsDeload: boolean;
  deloadReason?: string;
  recommendations: string[];
  confidenceLevel: 'low' | 'medium' | 'high';
}

function getValidSessionDate(session: SessionLog): Date | null {
  // Sessions created early in development or migrated data may not have a valid `date`.
  // Treat missing/invalid dates as unknown and EXCLUDE from time-window calculations.
  const raw = (session as any).date;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * Calculate comprehensive recovery debt score
 *
 * @param lookbackDays - Number of days to analyze (default: 28)
 * @param experienceLevel - User's training experience level (affects ACWR thresholds)
 */
export async function calculateRecoveryDebt(
  lookbackDays: number = 28,
  experienceLevel: ExperienceLevel = 'intermediate',
  asOf?: Date
): Promise<RecoveryDebtScore> {
  const allSessions = await getSessionHistory();

  // Get current program selection from Dexie (for informational purposes only)
  let currentProgramSelection: string | null = null;
  try {
    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      const userProfile = await db.userProfiles.get(currentUserId);
      currentProgramSelection = userProfile?.programSelection?.programId || null;
    }
  } catch (e) {
    console.warn(' Could not load program selection for ACWR calculation');
  }

  // FIXED: Use ALL PT/assessment sessions regardless of program
  // ACWR should track total training load, not just current program
  // This prevents the "45 sessions -> 0 sessions" bug
  const sessions = allSessions.filter(session => 
    session.sessionType === 'pt' || session.sessionType === 'assessment'
  );

  debugLog(` ACWR: Using ${sessions.length} of ${allSessions.length} sessions (all PT/assessment) | Current program: ${currentProgramSelection || '(none)'}`);
  
  // Debug: Log workload signals for filtered sessions
  // NOTE: Uses shared tonnage calculation from volumeTracking.ts for consistency
  const debugTonnage = calculateTotalSessionsTonnage(sessions);
  const debugInternal = calculateSessionsInternalLoad(sessions);
  debugLog(
    ` ACWR Debug: ${sessions.length} sessions | tonnage=${debugTonnage.toFixed(0)} lbs | internalLoad=${debugInternal.toFixed(0)} AU`
  );
  
  if (sessions.length > 0) {
    // Log recent session dates for debugging (sessions are sorted oldest-first)
    const recentDates = sessions
      .slice(-5)
      .map(s => getValidSessionDate(s)?.toLocaleDateString() || 'unknown');
    debugLog(` ACWR Debug: Recent session dates: ${recentDates.join(', ')}`);
  }
  
  if (sessions.length < 3) {
    return {
      overallDebt: 0,
      debtLevel: 'low',
      acwr: 1.0,
      acwrStatus: 'optimal',
      acwrDataSufficient: false,
      acuteLoad: 0,
      chronicLoad: 0,
      weeklyVolume: 0,
      last4WeekAverage: 0,
      volumeTrend: 'stable',
      forceBalance: {
        pushVolume: 0,
        pullVolume: 0,
        ratio: 1.0,
        status: 'balanced'
      },
      avgRPE: 7,
      rpeStatus: 'sustainable',
      consecutiveHighRPEDays: 0,
      needsDeload: false,
      recommendations: ['Complete more workouts to generate recovery insights'],
      confidenceLevel: 'low'
    };
  }
  
  // Filter sessions within lookback period.
  //
  // `asOf` defaults to now. It exists so the fatigue engine can be REPLAYED:
  // with a hardcoded `new Date()` a walk-forward backtest evaluates every
  // historical point against today's 28-day window, which is empty, so every
  // evaluation silently returned the no-data defaults (avgRPE 7.00,
  // consecutiveHighRPEDays 0, debtLevel 'low'). The audit's own success
  // criterion for 4.1 — "replay shouldDeload over the eligible days, target
  // 4-8 firings" — could not be checked at all until this parameter existed.
  const now = asOf ?? new Date();
  const cutoffDate = new Date(now.getTime() - (lookbackDays * 24 * 60 * 60 * 1000));
  const recentSessions = sessions
    .filter(s => {
      const sessionDate = getValidSessionDate(s);
      if (!sessionDate) return false;
      return sessionDate >= cutoffDate;
    })
    .sort((a, b) => {
      const dateA = getValidSessionDate(a);
      const dateB = getValidSessionDate(b);
      // Keep unknown dates last (but we filtered them out above anyway)
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.getTime() - dateB.getTime();
    });
  
  // Calculate ACWR (Acute:Chronic Workload Ratio)
  const acwrData = calculateACWR(recentSessions, experienceLevel);
  
  // Calculate volume trends
  const volumeData = calculateVolumeTrends(recentSessions);
  
  // Calculate force balance
  const forceBalance = calculateForceBalance(recentSessions);
  
  // Calculate RPE trends
  const rpeData = calculateRPETrends(recentSessions);
  
  // Load NEAT suppression data (non-critical, optional contribution)
  let neatSuppressionContribution = 0;
  try {
    const { getTodayActivity, getDailyActivityEntries } = await import('./db');
    const { detectNEATSuppression } = await import('./neatCalculator');
    const userId = getCurrentUserId();
    if (userId) {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const entries = await getDailyActivityEntries(userId, { since: thirtyDaysAgo });
      if (entries.length >= 14) {
        const suppression = detectNEATSuppression(entries as any);
        if (suppression?.detected) {
          // mild +3, moderate +6, severe +10
          neatSuppressionContribution =
            suppression.severity === 'severe' ? 10 :
            suppression.severity === 'moderate' ? 6 : 3;
        }
      }
    }
  } catch {
    // Non-critical: users without wearables see zero changes
  }

  // Determine overall debt level
  const debtScore = calculateOverallDebt(acwrData, volumeData, forceBalance, rpeData, neatSuppressionContribution);
  
  // Generate recommendations
  const recommendations = generateRecommendations(acwrData, volumeData, forceBalance, rpeData, debtScore);
  
  // Determine if deload is needed
  const needsDeload = shouldDeload(acwrData, volumeData, rpeData, debtScore);
  
  return {
    overallDebt: debtScore.score,
    debtLevel: debtScore.level,
    acwr: acwrData.ratio,
    acwrStatus: acwrData.status,
    acwrDataSufficient: acwrData.dataSufficient,
    acuteLoad: acwrData.acuteLoad,
    chronicLoad: acwrData.chronicLoad,
    weeklyVolume: volumeData.weeklyVolume,
    last4WeekAverage: volumeData.last4WeekAverage,
    volumeTrend: volumeData.trend,
    forceBalance,
    avgRPE: rpeData.avgRPE,
    rpeStatus: rpeData.status,
    consecutiveHighRPEDays: rpeData.consecutiveHighRPEDays,
    needsDeload,
    deloadReason: needsDeload ? generateDeloadReason(acwrData, volumeData, rpeData, debtScore) : undefined,
    recommendations,
    confidenceLevel: recentSessions.length >= 12 ? 'high' : recentSessions.length >= 6 ? 'medium' : 'low'
  };
}

/**
 * INTERNAL LOAD (TRIMP-equivalent without HR)
 *
 * We approximate internal load using Foster sRPE:
 *   internalLoad(AU) = sessionMinutes * sessionRPE
 *
 * sessionRPE is derived from logged exercise RPEs when available, otherwise falls back
 * to session.overallFatigue (1-10).
 */
function parseMMSS(value?: string): number {
  if (!value) return 0;
  const s = value.trim();
  // Support "MM:SS" or "HH:MM:SS"
  const parts = s.split(':').map(p => parseInt(p, 10));
  if (parts.some(n => Number.isNaN(n))) return 0;
  if (parts.length === 2) {
    const [mm, ss] = parts;
    return mm * 60 + ss;
  }
  if (parts.length === 3) {
    const [hh, mm, ss] = parts;
    return hh * 3600 + mm * 60 + ss;
  }
  return 0;
}

function isPrepOrInstructionExercise(ex: any): boolean {
  const block = (ex?.block || '').toString().toLowerCase();
  const format = (ex?.format || '').toString().toLowerCase();
  const name = (ex?.exerciseName || ex?.name || '').toString().toLowerCase();
  if (format.includes('instructions')) return true;
  /**
   * 'cool' was missing, and `Cool Down` is 176 of 1309 production exercise
   * logs — the single most common block after Movement Prep. Cool-down
   * stretches are logged at RPE 2-4, so they were being averaged into the
   * session RPE that shouldDeload() reads.
   *
   * Measured on ecorbin791's 118 scored sessions: excluding them raises the
   * mean session RPE from 7.005 to 7.279, matching the audit's independently
   * derived 0.31-point dilution. The mean understates the damage — 25
   * sessions shift, and the worst reads 5.43 when the working sets averaged
   * 8.00. A session that should count toward consecutive-high-RPE fatigue
   * was landing in the middle of the easy band.
   *
   * 'cool' also covers 'cooldown' and 'cool down' without a second test.
   */
  if (block.includes('prep') || block.includes('warm') || block.includes('cool') || block.includes('recovery')) return true;
  if (name.includes('instructions')) return true;
  return false;
}

function getSessionDurationMinutes(session: SessionLog): number {
  // Prefer explicit duration
  if (typeof (session as any).duration === 'number' && (session as any).duration > 0) {
    if (session.sessionType === 'range') {
      // Range sessions store minutes
      return (session as any).duration;
    }
    // PT/assessment/sprint store seconds
    return (session as any).duration / 60;
  }

  // Next: derive from timestamps
  const start = (session as any).startTime ? new Date((session as any).startTime) : null;
  const end = (session as any).endTime ? new Date((session as any).endTime) : null;
  if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
    const mins = (end.getTime() - start.getTime()) / (60 * 1000);
    if (mins > 0) return mins;
  }

  // Next: estimate from logged exercise times (best-effort)
  let seconds = 0;
  if ('exercises' in session && Array.isArray((session as any).exercises)) {
    for (const ex of (session as any).exercises) {
      seconds += parseMMSS(ex.actualTime);
      if (Array.isArray(ex.circuitSets)) {
        for (const r of ex.circuitSets) seconds += parseMMSS(r.time);
      }
      if (Array.isArray(ex.swimSets)) {
        for (const r of ex.swimSets) seconds += parseMMSS(r.time);
      }
      if (Array.isArray((ex as any).supersetRounds)) {
        for (const round of (ex as any).supersetRounds) {
          if (Array.isArray(round.exercises)) {
            // No time per superset exercise; ignore
          }
        }
      }
    }
  }

  const mins = seconds / 60;
  if (mins > 5) return mins;

  // Fallback default
  return session.sessionType === 'sprint' ? 20 : 45;
}

function getExerciseAvgRPE(ex: any): number | null {
  // Superset rounds (new structure)
  if (Array.isArray((ex as any).supersetRounds) && (ex as any).supersetRounds.length > 0) {
    const rpes: number[] = [];
    for (const round of (ex as any).supersetRounds) {
      if (!round?.exercises) continue;
      for (const e of round.exercises) {
        if (typeof e?.rpe === 'number') rpes.push(e.rpe);
      }
    }
    if (rpes.length > 0) return rpes.reduce((a, b) => a + b, 0) / rpes.length;
  }

  // Per-set RPE
  if (Array.isArray(ex.sets) && ex.sets.length > 0) {
    const rpes = ex.sets.map((s: any) => s?.rpe).filter((v: any) => typeof v === 'number');
    if (rpes.length > 0) return rpes.reduce((a: number, b: number) => a + b, 0) / rpes.length;
  }

  // Circuit/swim sets
  if (Array.isArray(ex.circuitSets) && ex.circuitSets.length > 0) {
    const rpes = ex.circuitSets.map((s: any) => s?.rpe).filter((v: any) => typeof v === 'number');
    if (rpes.length > 0) return rpes.reduce((a: number, b: number) => a + b, 0) / rpes.length;
  }
  if (Array.isArray(ex.swimSets) && ex.swimSets.length > 0) {
    const rpes = ex.swimSets.map((s: any) => s?.rpe).filter((v: any) => typeof v === 'number');
    if (rpes.length > 0) return rpes.reduce((a: number, b: number) => a + b, 0) / rpes.length;
  }

  // Legacy single-value
  if (typeof ex.actualRPE === 'number' && ex.actualRPE > 0) return ex.actualRPE;
  return null;
}

function getSessionSRPE(session: SessionLog): number | null {
  // Range sessions don't have exercises; use fatigue as proxy
  if (session.sessionType === 'range') {
    const f = (session as any).overallFatigue;
    return typeof f === 'number' && f > 0 ? f : null;
  }

  if (!('exercises' in session) || !Array.isArray((session as any).exercises)) return null;
  const rpes: number[] = [];
  for (const ex of (session as any).exercises) {
    if (isPrepOrInstructionExercise(ex)) continue;
    const r = getExerciseAvgRPE(ex);
    if (typeof r === 'number' && r > 0) rpes.push(r);
  }
  if (rpes.length > 0) return rpes.reduce((a, b) => a + b, 0) / rpes.length;

  const fatigue = (session as any).overallFatigue;
  return typeof fatigue === 'number' && fatigue > 0 ? fatigue : null;
}

function calculateSessionInternalLoad(session: SessionLog): number {
  const minutes = getSessionDurationMinutes(session);
  const srpe = getSessionSRPE(session);
  if (!srpe || minutes <= 0) return 0;
  return minutes * srpe;
}

function calculateSessionsInternalLoad(sessions: SessionLog[]): number {
  return sessions.reduce((sum, s) => sum + calculateSessionInternalLoad(s), 0);
}

/**
 * Calculate Acute:Chronic Workload Ratio (ACWR)
 * 
 * ACWR compares short-term load to longer-term load:
 * 
 * - Acute: last 7 days
 * - Chronic: last 28 days
 * 
 * We use an **EWMA** (Exponentially Weighted Moving Average) model:
 * \(ACWR = \frac{EWMA_{7d}}{EWMA_{28d}}\)
 *
 * Why EWMA:
 * - Rest days (0 load) reduce acute load smoothly (no "stuck" windows)
 * - Better matches real-world fatigue decay and user expectations
 * 
 * Notes:
 * - Injury is multifactorial; ACWR is a heuristic, not a diagnosis.
 * - RA is simpler but can be "step-wise" and feel wrong during rest streaks.
 * 
 * Thresholds are scaled by experience level:
 * - Beginners: 0.8-1.5 (can handle spikes due to low baseline)
 * - Intermediate: 0.8-1.35 (standard thresholds)
 * - Advanced: 0.75-1.4 (slightly higher tolerance)
 * - Elite: 0.7-1.5 (adapted to sustained high loads)
 */
function calculateACWR(sessions: SessionLog[], experienceLevel: ExperienceLevel = 'intermediate'): {
  ratio: number;
  status: 'undertrained' | 'optimal' | 'elevated' | 'dangerous';
  dataSufficient: boolean;
  acuteLoad: number;
  chronicLoad: number;
} {
  const now = new Date();

  // Build daily loads for the last 28 days (inclusive), so rest days count as 0.
  // Use local day boundaries to avoid timezone edge cases.
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const startOfWindow = new Date(endOfToday.getTime() - (27 * 24 * 60 * 60 * 1000)); // 28 days inclusive
  const windowDayStart = new Date(startOfWindow.getFullYear(), startOfWindow.getMonth(), startOfWindow.getDate());

  const dailyLoads = new Array<number>(28).fill(0);

  for (const s of sessions) {
    const d = getValidSessionDate(s);
    if (!d) continue;
    if (d < startOfWindow || d > endOfToday) continue;

    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dayIndex = Math.floor((dayStart.getTime() - windowDayStart.getTime()) / (24 * 60 * 60 * 1000));
    if (dayIndex < 0 || dayIndex >= 28) continue;

    // Add this session's INTERNAL load (AU) to that day
    dailyLoads[dayIndex] += calculateSessionInternalLoad(s);
  }

  // EWMA alphas (common: 2/(N+1))
  const acuteAlpha = 2 / (7 + 1);
  const chronicAlpha = 2 / (28 + 1);

  let acuteEWMA = dailyLoads[0];
  let chronicEWMA = dailyLoads[0];
  for (let i = 1; i < dailyLoads.length; i++) {
    acuteEWMA = acuteAlpha * dailyLoads[i] + (1 - acuteAlpha) * acuteEWMA;
    chronicEWMA = chronicAlpha * dailyLoads[i] + (1 - chronicAlpha) * chronicEWMA;
  }

  // Store loads as "weekly-equivalent" for UI readability (AU/week).
  const acuteLoad = acuteEWMA * 7;
  const chronicLoad = chronicEWMA * 7;

  debugLog(` ACWR (EWMA): Window=${startOfWindow.toLocaleDateString()} → ${endOfToday.toLocaleDateString()}`);
  debugLog(` ACWR (EWMA): acute≈${acuteLoad.toFixed(0)} AU/wk, chronic≈${chronicLoad.toFixed(0)} AU/wk`);

  const nonZeroDays = dailyLoads.filter(x => x > 0).length;
  if (nonZeroDays < 4) {
    debugLog(` ACWR (EWMA): Not enough data (${nonZeroDays} training days in last 28d) - returning neutral 1.0`);
    return { ratio: 1.0, status: 'optimal', dataSufficient: false, acuteLoad, chronicLoad };
  }

  if (chronicEWMA <= 0) {
    debugLog(` ACWR (EWMA): No chronic load - returning neutral 1.0`);
    return { ratio: 1.0, status: 'optimal', dataSufficient: false, acuteLoad, chronicLoad: 0 };
  }

  const ratio = acuteEWMA / chronicEWMA;

  debugLog(` ACWR (EWMA): Ratio = ${ratio.toFixed(2)} (acuteEWMA/chronicEWMA)`);

  // Determine status using experience-scaled thresholds
  const status = getACWRStatus(ratio, experienceLevel);

  return { ratio, status, dataSufficient: true, acuteLoad, chronicLoad };
}

/**
 * Detect if a session is a deload session
 * Checks multiple indicators: explicit deload flag, low volume, low RPE
 * 
 * ENHANCED with heuristic detection for sessions without explicit flags
 */
function isDeloadSession(session: SessionLog, allSessions?: SessionLog[]): boolean {
  // Check explicit deload flag (if available from program data)
  if ('isDeload' in session && session.isDeload === true) {
    return true;
  }
  
  // Check if session type indicates deload
  if (session.sessionType === 'range' || session.sessionType === 'sprint') {
    return false; // Range/sprint days are not deload
  }
  
  // Type guard: only PT/assessment sessions have exercises
  if (session.sessionType !== 'pt' && session.sessionType !== 'assessment') {
    return false;
  }
  
  // Check if session has exercises
  if (!('exercises' in session) || !session.exercises || session.exercises.length === 0) {
    return false; // Empty session, not a deload
  }
  
  const allSetsLowRPE = session.exercises.every(ex => {
    if (ex.sets && ex.sets.length > 0) {
      // Check per-set RPE
      return ex.sets.every(set => set.rpe !== null && set.rpe < 6);
    } else if (ex.actualRPE !== undefined) {
      // Check legacy RPE
      return ex.actualRPE < 6;
    }
    return false; // No RPE data, can't determine
  });
  
  if (allSetsLowRPE && session.exercises.length > 0) {
    return true; // All exercises at low RPE = deload
  }
  
  // ===== HEURISTIC 2: Low volume compared to recent average =====
  // If session volume is < 50% of user's 7-day average, likely a deload
  if (allSessions && allSessions.length >= 3) {
    const sessionVolume = calculateSessionsWorkload([session]);
    
    // Calculate average volume from last 7 days (excluding this session)
    const now = getValidSessionDate(session);
    if (!now) return false;
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    
    const recentSessions = allSessions.filter(s => {
      const sDate = getValidSessionDate(s);
      if (!sDate) return false;
      return sDate >= sevenDaysAgo && 
             sDate < now && 
             s.sessionType !== 'range' && 
             s.sessionType !== 'sprint' &&
             s.exercises && 
             s.exercises.length > 0;
    });
    
    if (recentSessions.length >= 2) {
      const totalRecentVolume = calculateSessionsWorkload(recentSessions);
      const avgVolume = totalRecentVolume / recentSessions.length;
      
      // If this session is < 50% of average, it's a deload
      if (avgVolume > 0 && sessionVolume < avgVolume * 0.5) {
        return true;
      }
    }
  }
  
  // ===== HEURISTIC 3: Significantly fewer sets than usual =====
  // If total sets < 50% of recent average, likely a deload
  // At this point we know session has exercises from the checks above
  const totalSets = ('exercises' in session && session.exercises) ? session.exercises.reduce((sum, ex) => {
    if (ex.sets && ex.sets.length > 0) {
      return sum + ex.sets.filter(s => s.completed).length;
    } else if (ex.targetSets) {
      // Parse legacy format like "3x5"
      const match = ex.targetSets.match(/(\d+)/);
      return sum + (match ? parseInt(match[1]) : 0);
    }
    return sum;
  }, 0) : 0;
  
  if (allSessions && allSessions.length >= 3) {
    const now = getValidSessionDate(session);
    if (!now) return false;
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    
    const recentSessions = allSessions.filter(s => {
      const sDate = getValidSessionDate(s);
      if (!sDate) return false;
      return sDate >= sevenDaysAgo && 
             sDate < now && 
             s.sessionType !== 'range' && 
             s.sessionType !== 'sprint' &&
             s.exercises && 
             s.exercises.length > 0;
    });
    
    if (recentSessions.length >= 2) {
      const avgSets = recentSessions.reduce((sum, s) => {
        if (!('exercises' in s) || !s.exercises || s.exercises.length === 0) return sum;
        const sSets = s.exercises.reduce((eSum, ex) => {
          if (ex.sets && ex.sets.length > 0) {
            return eSum + ex.sets.filter(set => set.completed).length;
          } else if (ex.targetSets) {
            const match = ex.targetSets.match(/(\d+)/);
            return eSum + (match ? parseInt(match[1]) : 0);
          }
          return eSum;
        }, 0);
        return sum + sSets;
      }, 0) / recentSessions.length;
      
      // If this session has < 50% of average sets, it's a deload
      if (avgSets > 0 && totalSets < avgSets * 0.5) {
        return true;
      }
    }
  }
  
  return false; // Default to not deload unless heuristics match
}

/**
 * Calculate total workload from sessions (tonnage)
 * 
 * IMPORTANT: Counts all sets with valid weight/reps data, regardless of `completed` status.
 * This ensures partial workouts are accurately tracked in volume and ACWR calculations.
 * 
 * Rationale: If a user logged weight and reps, they performed the set. The `completed` 
 * checkbox is for UI feedback, not for determining if work was done.
 */
function calculateSessionsWorkload(sessions: SessionLog[]): number {
  let totalWorkload = 0;
  
  sessions.forEach(session => {
    if (session.sessionType === 'range' || !session.exercises) return;
    
    session.exercises.forEach(exercise => {
      let exerciseWorkload = 0;
      
      // Handle superset rounds (for circuit/superset exercises)
      if ((exercise as any).supersetRounds && (exercise as any).supersetRounds.length > 0) {
        (exercise as any).supersetRounds.forEach((round: any) => {
          if (round.exercises && round.exercises.length > 0) {
            round.exercises.forEach((ex: any) => {
              if (ex.weight > 0 && ex.reps > 0) {
                exerciseWorkload += computeSetVolume(ex, ex.name || exercise.exerciseName);
              }
            });
          }
        });
      }
      // Handle regular sets
      else if (exercise.sets && exercise.sets.length > 0) {
        // Calculate per-set volume
        exercise.sets.forEach(set => {
          // Count any set with valid weight/reps, regardless of completed status
          // This ensures partial workouts contribute to volume tracking
          if (set.weight > 0 && set.reps > 0) {
            exerciseWorkload += computeSetVolume(set, exercise.exerciseName);
          }
        });
      }
      
      // Fallback: Use legacy single-value tracking if per-set data isn't available
      // This handles older sessions that used actualLoad/actualReps
      if (exerciseWorkload === 0) {
        const legacyLoad = (exercise as any).actualLoad || exercise.targetLoad || 0;
        const legacyReps = (exercise as any).actualReps || 0;
        
        if (legacyLoad > 0 && legacyReps > 0) {
          // Parse number of sets from targetSets (e.g., "3x5")
          const setsMatch = exercise.targetSets?.match(/^(\d+)/);
          const numSets = setsMatch ? parseInt(setsMatch[1]) : 1;
          exerciseWorkload = legacyLoad * legacyReps * numSets;
        }
        
        // For completed exercises without detailed data, estimate from target
        // This prevents sessions from being counted as 0 workload
        if (exerciseWorkload === 0 && exercise.completed && exercise.targetLoad > 0) {
          const setsMatch = exercise.targetSets?.match(/^(\d+)\s*[x×]\s*(\d+)/i);
          if (setsMatch) {
            const sets = parseInt(setsMatch[1]);
            const reps = parseInt(setsMatch[2]);
            exerciseWorkload = exercise.targetLoad * sets * reps;
          }
        }
      }
      
      totalWorkload += exerciseWorkload;
    });
  });
  
  return totalWorkload;
}

/**
 * Calculate volume trends over recent weeks
 */
function calculateVolumeTrends(sessions: SessionLog[]): {
  weeklyVolume: number;
  last4WeekAverage: number;
  trend: 'increasing' | 'stable' | 'decreasing';
} {
  if (sessions.length < 4) {
    return { weeklyVolume: 0, last4WeekAverage: 0, trend: 'stable' };
  }
  
  const now = new Date();
  
  // This week's volume
  const weekCutoff = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
  const weekSessions = sessions.filter(s => {
    const date = getValidSessionDate(s);
    if (!date) return false;
    return date >= weekCutoff;
  });
  const weeklyVolume = calculateSessionsWorkload(weekSessions);
  
  // Last 4 weeks average
  const fourWeekCutoff = new Date(now.getTime() - (28 * 24 * 60 * 60 * 1000));
  const fourWeekSessions = sessions.filter(s => {
    const date = getValidSessionDate(s);
    if (!date) return false;
    return date >= fourWeekCutoff;
  });
  const totalFourWeekVolume = calculateSessionsWorkload(fourWeekSessions);
  const last4WeekAverage = totalFourWeekVolume / 4;
  
  // Determine trend
  let trend: 'increasing' | 'stable' | 'decreasing';
  const percentChange = last4WeekAverage > 0 
    ? ((weeklyVolume - last4WeekAverage) / last4WeekAverage) * 100 
    : 0;
  
  if (percentChange > 10) {
    trend = 'increasing';
  } else if (percentChange < -10) {
    trend = 'decreasing';
  } else {
    trend = 'stable';
  }
  
  return { weeklyVolume, last4WeekAverage, trend };
}

/**
 * Calculate force balance (push/pull ratio)
 * Ideal ratio: 0.8-1.2 (slightly pull-dominant is healthy for posture)
 */
function calculateForceBalance(sessions: SessionLog[]): {
  pushVolume: number;
  pullVolume: number;
  ratio: number;
  status: 'balanced' | 'push-dominant' | 'pull-dominant';
  recommendation?: string;
} {
  let pushVolume = 0;
  let pullVolume = 0;
  
  const now = new Date();
  const fourWeekCutoff = new Date(now.getTime() - (28 * 24 * 60 * 60 * 1000));
  const recentSessions = sessions.filter(s => {
    const date = getValidSessionDate(s);
    if (!date) return false;
    return date >= fourWeekCutoff;
  });
  
  recentSessions.forEach(session => {
    if (session.sessionType === 'range' || !session.exercises) return;
    
    session.exercises.forEach(exercise => {
      const metadata = getExerciseMetadata(exercise.exerciseName);
      let exerciseVolume = 0;
      
      // Handle superset rounds (for circuit/superset exercises)
      if ((exercise as any).supersetRounds && (exercise as any).supersetRounds.length > 0) {
        (exercise as any).supersetRounds.forEach((round: any) => {
          if (round.exercises && round.exercises.length > 0) {
            round.exercises.forEach((ex: any) => {
              if (ex.weight > 0 && ex.reps > 0) {
                const volume = computeSetVolume(ex, ex.name || exercise.exerciseName);
                exerciseVolume += volume;
                
                // Try to get metadata for the individual exercise in the superset
                const exMetadata = getExerciseMetadata(ex.name || exercise.exerciseName);
                if (exMetadata?.force === 'push') {
                  pushVolume += volume;
                } else if (exMetadata?.force === 'pull') {
                  pullVolume += volume;
                }
              }
            });
          }
        });
      }
      // Handle regular sets
      else if (exercise.sets && exercise.sets.length > 0) {
        exerciseVolume = exercise.sets.reduce((sum, set) => {
          // Count all sets with valid weight/reps, regardless of completed status
          return sum + (set.weight > 0 && set.reps > 0 ? computeSetVolume(set, exercise.exerciseName) : 0);
        }, 0);
        
        if (metadata?.force === 'push') {
          pushVolume += exerciseVolume;
        } else if (metadata?.force === 'pull') {
          pullVolume += exerciseVolume;
        }
      }
    });
  });
  
  const ratio = pullVolume > 0 ? pushVolume / pullVolume : 1.0;
  
  let status: 'balanced' | 'push-dominant' | 'pull-dominant';
  let recommendation: string | undefined;
  
  if (ratio >= 0.8 && ratio <= 1.2) {
    status = 'balanced';
  } else if (ratio > 1.2) {
    status = 'push-dominant';
    recommendation = `Add more pulling exercises (rows, pull-ups) to prevent shoulder imbalances. Current ratio: ${ratio.toFixed(2)}:1`;
  } else {
    status = 'pull-dominant';
    recommendation = `Add more pushing exercises (pressing, dips) to balance upper body development. Current ratio: ${ratio.toFixed(2)}:1`;
  }
  
  return { pushVolume, pullVolume, ratio, status, recommendation };
}

/**
 * Calculate RPE trends
 */
function calculateRPETrends(sessions: SessionLog[]): {
  avgRPE: number;
  status: 'sustainable' | 'elevated' | 'unsustainable';
  consecutiveHighRPEDays: number;
} {
  if (sessions.length === 0) {
    return { avgRPE: 7, status: 'sustainable', consecutiveHighRPEDays: 0 };
  }
  
  // Calculate average RPE across all exercises in recent sessions
  const allRPEs: number[] = [];
  sessions.forEach(session => {
    if (session.sessionType === 'range' || !session.exercises) return;
    
    session.exercises.forEach(exercise => {
      /**
       * Prep, warm-up and cool-down do not describe how hard the session was.
       *
       * This function feeds shouldDeload() — it is THE fatigue signal — and it
       * was reading the RPE of every stretch and mobility drill in the log.
       * `Movement Prep` (219 logs), `Cool Down` (176) and `Warm-Up` (112) are
       * three of the four most common blocks in production, together 40% of
       * all exercise logs, and they are recorded at RPE 2-4.
       *
       * `isPrepOrInstructionExercise` already existed and was already applied
       * to the OTHER session-RPE helper in this file. It was simply never
       * wired into this one, so the fix to that helper never reached the
       * deload path. Measured on ecorbin791: excluding cool-downs alone moves
       * the mean session RPE 7.005 -> 7.279, and the worst-affected session
       * from 5.43 to 8.00 — the difference between an easy day and a
       * deload-triggering one.
       */
      if (isPrepOrInstructionExercise(exercise)) return;

      // Handle superset rounds (for circuit/superset exercises)
      if ((exercise as any).supersetRounds && (exercise as any).supersetRounds.length > 0) {
        (exercise as any).supersetRounds.forEach((round: any) => {
          if (round.exercises && round.exercises.length > 0) {
            round.exercises.forEach((ex: any) => {
              if (ex.rpe !== null && ex.rpe !== undefined) {
                allRPEs.push(ex.rpe);
              }
            });
          }
        });
      }
      // Handle regular sets
      else if (exercise.sets && exercise.sets.length > 0) {
        exercise.sets.forEach(set => {
          if (set.rpe !== null && set.rpe !== undefined) {
            allRPEs.push(set.rpe);
          }
        });
      }
    });
  });
  
  const avgRPE = allRPEs.length > 0 
    ? allRPEs.reduce((sum, rpe) => sum + rpe, 0) / allRPEs.length 
    : 7;
  
  // Check for consecutive high RPE sessions
  let consecutiveHighRPEDays = 0;
  /**
   * Ten sessions, not seven.
   *
   * The counter needs enough RATED sessions in its window to be able to reach
   * its own threshold. Measured on production: the median trailing-7 window
   * contains only FOUR sessions carrying any RPE (50 of 139 sessions have
   * none at all), so a `>= 5 consecutive` rule was arithmetically impossible
   * more than half the time — before any threshold was even considered.
   */
  const recentSessions = sessions.slice(-10);
  for (let i = recentSessions.length - 1; i >= 0; i--) {
    const session = recentSessions[i];
    const sessionRPEs: number[] = [];
    
    if (session.sessionType !== 'range' && session.exercises) {
      session.exercises.forEach(exercise => {
        /**
         * Same prep/cool-down exclusion as the average above.
         *
         * This loop keeps its OWN copy of the RPE collection, so fixing the
         * average alone left the streak counter — which feeds
         * `consecutiveHighRPEDays >= 4`, the PRIMARY deload trigger — still
         * averaging cool-down stretches into every session. A genuinely hard
         * day rated 8.5 across its working sets lands at 6.8 once three
         * RPE-3 stretches are averaged in, fails the >= 8.0 test, and breaks
         * a streak that should have continued.
         *
         * Caught in the 2026-08-12 back-audit of the fix directly above it.
         */
        if (isPrepOrInstructionExercise(exercise)) return;

        // Handle superset rounds (for circuit/superset exercises)
        if ((exercise as any).supersetRounds && (exercise as any).supersetRounds.length > 0) {
          (exercise as any).supersetRounds.forEach((round: any) => {
            if (round.exercises && round.exercises.length > 0) {
              round.exercises.forEach((ex: any) => {
                if (ex.rpe !== null && ex.rpe !== undefined) {
                  sessionRPEs.push(ex.rpe);
                }
              });
            }
          });
        }
        // Handle regular sets
        else if (exercise.sets && exercise.sets.length > 0) {
        exercise.sets.forEach(set => {
          if (set.rpe !== null && set.rpe !== undefined) {
            sessionRPEs.push(set.rpe);
          }
        });
      }
    });
    }
    
    /**
     * An UNRATED session is not evidence of freshness.
     *
     * This defaulted to 7 when a session carried no RPE at all, and 7 fails
     * the test below, so the `break` fired and the streak reset to zero. One
     * session logged without RPE therefore erased however much accumulated
     * fatigue the counter had seen. Skip it instead — absence of evidence is
     * not evidence of an easy day.
     */
    if (sessionRPEs.length === 0) continue;

    const sessionAvgRPE =
      sessionRPEs.reduce((sum, rpe) => sum + rpe, 0) / sessionRPEs.length;

    /**
     * HIGH = session average RPE >= 8.0 (roughly RIR 2 or less).
     *
     * This was 8.5, which made the `>= 5 consecutive` trigger unreachable in
     * practice: across 89 RPE-carrying production sessions the longest run at
     * >= 8.5 was TWO. At >= 8.0 the longest run is exactly five, so the
     * trigger becomes rare-but-reachable rather than decorative — five
     * straight sessions averaging RIR <= 2 is genuine accumulation.
     *
     * NOTE: this counts SESSIONS, not calendar days, despite the field name.
     * A rest day between two hard sessions does not break the run, which is
     * the behaviour we want for fatigue accumulation but is worth knowing
     * when reading the number.
     */
    if (sessionAvgRPE >= 8.0) {
      consecutiveHighRPEDays++;
    } else {
      break;
    }
  }
  
  // Determine status
  let status: 'sustainable' | 'elevated' | 'unsustainable';
  if (avgRPE < 8.0) {
    status = 'sustainable';
  } else if (avgRPE >= 8.0 && avgRPE < 8.5) {
    status = 'elevated';
  } else {
    status = 'unsustainable';
  }
  
  return { avgRPE, status, consecutiveHighRPEDays };
}

/**
 * Calculate overall recovery debt score (0-100)
 */
function calculateOverallDebt(
  acwrData: any,
  volumeData: any,
  forceBalance: any,
  rpeData: any,
  neatSuppressionContribution: number = 0
): { score: number; level: 'low' | 'moderate' | 'high' | 'severe' } {
  let score = 0;

  // NOTE: ACWR is deliberately NOT a debt contributor. The acute:chronic ratio
  // has no demonstrated predictive value for injury/load management — its
  // apparent signal is a mathematical artifact of coupling (Impellizzeri 2020,
  // Sports Med; Coyne 2021 "Time to Dismiss ACWR", Sports Med). The legitimate
  // thing it tried to proxy — a sharp ACUTE LOAD SPIKE — is captured directly
  // below via the uncoupled week-over-week volume change.

  // RPE contribution (0-30 points)
  if (rpeData.status === 'unsustainable') {
    score += 30;
  } else if (rpeData.status === 'elevated') {
    score += 15;
  }

  // Consecutive high RPE days (0-15 points)
  score += Math.min(rpeData.consecutiveHighRPEDays * 3, 15);

  // Acute load spike — uncoupled week-over-week volume increase (0-40 points).
  // A large jump in weekly load is a conservative fatigue-management flag
  // (general progressive-overload guidance caps weekly increases near ~10%).
  if (volumeData.trend === 'increasing') {
    const percentChange = volumeData.last4WeekAverage > 0
      ? ((volumeData.weeklyVolume - volumeData.last4WeekAverage) / volumeData.last4WeekAverage) * 100
      : 0;

    if (percentChange > 50) {
      score += 40; // very rapid acute load spike
    } else if (percentChange > 30) {
      score += 25;
    } else if (percentChange > 15) {
      score += 12;
    }
  }

  // NEAT suppression contribution (0-10 points)
  score += neatSuppressionContribution;

  // Determine level
  let level: 'low' | 'moderate' | 'high' | 'severe';
  if (score < 25) {
    level = 'low';
  } else if (score < 50) {
    level = 'moderate';
  } else if (score < 75) {
    level = 'high';
  } else {
    level = 'severe';
  }
  
  return { score: Math.min(score, 100), level };
}

/**
 * Determine if a deload is needed
 */
function shouldDeload(
  acwrData: any,
  volumeData: any,
  rpeData: any,
  debtScore: any
): boolean {
  // Deload triggers are UNCOUPLED fatigue signals only. ACWR status is not used
  // here — the acute:chronic ratio has no demonstrated predictive value
  // (Impellizzeri 2020; Coyne 2021), so it must not gate a deload on its own.
  /**
   * FOUR consecutive hard sessions, not five.
   *
   * Calibrated by replaying this rule across 139 production sessions:
   *
   *   window  thr  need   fires
   *        7  8.5     5       0   <- shipped behaviour; never fired in 244 days
   *        7  8.0     5       0
   *       10  8.0     5       0
   *       10  8.0     4       6   <- chosen
   *       10  7.5     4      40   <- far too loose; RPE 7.5 is a normal hard set
   *
   * CORRECTION (2026-08-12, from the walk-forward replay in
   * lib/__tests__/sim/backtest.realdata.test.ts): those "6 fires" were 6
   * consecutive DAYS, which is ONE deload episode, not six. A deload that
   * stays true for a week is a single event; counting days flattered the
   * calibration by 6x. Replayed properly over 130 evaluations, with prep and
   * cool-down RPE now correctly excluded:
   *
   *   evaluations 130 | firing days 6 | distinct episodes 1 (2026-02-12)
   *   max consecutive high-RPE days 4 (threshold is >= 4 — it only just
   *   reaches) | max 28-day avgRPE 8.21 | debtLevel never exceeded 'moderate'
   *
   * DELIBERATELY NOT RETUNED to hit "4-8". That figure comes from Bell et
   * al.'s guidance on SCHEDULED deloads, and quoting it here conflated two
   * different mechanisms. Routine deloads come from the program's own
   * periodization (see isDeloadWeek in smartWorkoutGenerator), which was
   * itself dead until 2026-08-11 and is now live. This is the BACKSTOP for
   * fatigue the schedule did not anticipate: one episode in ~35 weeks,
   * alongside working scheduled deloads, is what a backstop SHOULD look like.
   * Loosening it to reach 4-8 would double-deload an athlete whose program
   * already deloads on schedule.
   *
   * What matters is that it is reachable and evidenced, which it now is —
   * before 2026-08-11 it fired 0 times in 244 days, and before the `asOf`
   * parameter existed nobody could even measure it.
   *
   * CAVEAT: calibrated against ONE athlete's history. Revisit when there is
   * data from more users.
   */
  if (rpeData.consecutiveHighRPEDays >= 4) return true;   // sustained max effort
  if (rpeData.status === 'unsustainable') return true;    // RPE trend unsustainable
  if (debtScore.level === 'severe') return true;          // composite fatigue (RPE + load spike + NEAT)
  if (debtScore.level === 'high' && rpeData.consecutiveHighRPEDays >= 3) return true;

  return false;
}

/**
 * Generate deload reason
 */
function generateDeloadReason(acwrData: any, volumeData: any, rpeData: any, debtScore: any): string {
  const reasons: string[] = [];

  // ACWR is intentionally omitted — deloads are justified by uncoupled fatigue
  // signals (RPE trend, recovery debt, acute load spike), not the ratio.
  const volPctChange = volumeData?.last4WeekAverage > 0
    ? ((volumeData.weeklyVolume - volumeData.last4WeekAverage) / volumeData.last4WeekAverage) * 100
    : 0;
  if (volPctChange > 30) {
    reasons.push(`Weekly load up ${Math.round(volPctChange)}% vs your 4-week average`);
  }

  if (rpeData.consecutiveHighRPEDays >= 3) {
    reasons.push(`${rpeData.consecutiveHighRPEDays} consecutive days at high RPE`);
  }
  
  if (rpeData.status === 'unsustainable') {
    reasons.push(`Average RPE ${rpeData.avgRPE.toFixed(1)}/10 (unsustainable)`);
  }
  
  if (debtScore.level === 'severe' || debtScore.level === 'high') {
    reasons.push(`Recovery debt at ${debtScore.level} level (${debtScore.score}/100)`);
  }
  
  return reasons.join(' • ');
}

/**
 * Generate actionable recommendations
 */
function generateRecommendations(
  acwrData: any,
  volumeData: any,
  forceBalance: any,
  rpeData: any,
  debtScore: any
): string[] {
  const recommendations: string[] = [];
  
  // ACWR recommendations
  if (acwrData.status === 'dangerous' || acwrData.status === 'elevated') {
    recommendations.push(' Reduce training volume by 30-40% for 1 week');
  } else if (acwrData.status === 'undertrained') {
    recommendations.push(' Gradually increase training volume by 5-10% per week');
  } else {
    recommendations.push(' Training volume is in optimal range');
  }
  
  // RPE recommendations
  if (rpeData.status === 'unsustainable') {
    recommendations.push(' Reduce intensity - target RPE 6-7 for next 5 workouts');
  } else if (rpeData.status === 'elevated') {
    recommendations.push(' Consider easier variations or reduce sets by 1-2');
  } else {
    recommendations.push(' Training intensity is sustainable');
  }
  
  // Force balance recommendations
  if (forceBalance.status !== 'balanced' && forceBalance.recommendation) {
    recommendations.push(` ${forceBalance.recommendation}`);
  } else {
    recommendations.push(' Push/pull balance is healthy');
  }
  
  // Volume trend recommendations
  if (volumeData.trend === 'increasing') {
    const percentChange = volumeData.last4WeekAverage > 0
      ? ((volumeData.weeklyVolume - volumeData.last4WeekAverage) / volumeData.last4WeekAverage) * 100
      : 0;
    
    if (percentChange > 20) {
      recommendations.push(` Volume increased ${percentChange.toFixed(0)}% - monitor for overtraining`);
    }
  } else if (volumeData.trend === 'decreasing') {
    recommendations.push(' Volume decreasing - ensure adequate training stimulus');
  }
  
  // Recovery debt recommendations
  if (debtScore.level === 'low') {
    recommendations.push(' Good recovery status - maintain current approach');
  } else if (debtScore.level === 'moderate') {
    recommendations.push(' Monitor fatigue - add 1-2 active recovery days');
  }
  
  return recommendations;
}

/**
 * Get quick recovery debt status for dashboard
 */
export async function getQuickRecoveryStatus(): Promise<{
  status: 'good' | 'caution' | 'warning' | 'critical';
  message: string;
  needsAttention: boolean;
}> {
  const debt = await calculateRecoveryDebt();
  
  if (debt.debtLevel === 'severe') {
    return {
      status: 'critical',
      message: `Critical: Recovery debt at ${debt.overallDebt}/100. Deload required.`,
      needsAttention: true
    };
  } else if (debt.debtLevel === 'high') {
    return {
      status: 'warning',
      message: `High fatigue: ${debt.overallDebt}/100 debt. Consider deload week.`,
      needsAttention: true
    };
  } else if (debt.debtLevel === 'moderate') {
    return {
      status: 'caution',
      message: `Moderate fatigue: Monitor recovery and sleep quality.`,
      needsAttention: false
    };
  } else {
    return {
      status: 'good',
      message: `Recovery status: Optimal. Training load is sustainable.`,
      needsAttention: false
    };
  }
}

